import { useMemo } from "react";
import { StringLike } from "@visx/scale";

import Box from "ds/components/Box";
import Card from "ds/components/Card";
import PageInfoHeader from "ds/components/PageInfoHeader";
import SegmentedControl from "ds/components/SegmentedControl";
import { GroupByKey } from "views/Account/Settings/Usage/Chart/constants";
import UsageChartView from "views/Account/Settings/Usage/Chart/View";
import { getLegendTitleByGroup } from "views/Account/Settings/Usage/Chart/utils";
import CSVDownloadDrawerButton from "views/Account/Settings/Usage/CSVDownload/DrawerButton";
import { getCSVDownloadRange } from "views/Account/Settings/Usage/CSVDownload/utils";
import UsageLayoutHeader from "views/Account/Settings/Usage/Layout/Header";

import { getColorMapper } from "../colorMapper";
import RunMinutesTotalsView from "../Totals/View";
import { RunMinutesUsageData } from "../types";
import {
  adaptRunMinutesUsageToChart,
  formatRunMinutesUsageDate,
  getRunMinutesUsageChartItems,
  getRunMinutesUsageWindowCaption,
} from "../utils";
import styles from "./styles.module.css";

type UsageRunMinutesContentProps = {
  usage: RunMinutesUsageData;
  groupBy: GroupByKey;
  onGroupByChange: (value: string) => void;
};

const GROUP_BY_OPTIONS = [
  { value: GroupByKey.RunType, label: "Type" },
  { value: GroupByKey.RunState, label: "State" },
];

const formatXAxisLabel = (value: StringLike) =>
  formatRunMinutesUsageDate(Number(value), "EEE d MMM");

const UsageRunMinutesContent = ({
  usage,
  groupBy,
  onGroupByChange,
}: UsageRunMinutesContentProps) => {
  const series = groupBy === GroupByKey.RunState ? usage.byRunState : usage.byRunType;

  const items = useMemo(() => getRunMinutesUsageChartItems(series), [series]);
  const chartData = useMemo(() => adaptRunMinutesUsageToChart(series, items), [series, items]);

  return (
    <>
      <UsageLayoutHeader />

      <PageInfoHeader title="Runs" caption={getRunMinutesUsageWindowCaption(usage)}>
        <CSVDownloadDrawerButton
          aspectType="run-minutes"
          initialRange={getCSVDownloadRange(usage.windowStart, usage.windowEnd)}
        />
      </PageInfoHeader>

      <Box direction="column" padding="x-large" gap="x-large" grow="1">
        <RunMinutesTotalsView
          privateMinutes={usage.privateMinutes}
          publicMinutes={usage.publicMinutes}
        />

        <Card
          title="Run minutes"
          headerSuffix={
            <SegmentedControl
              options={GROUP_BY_OPTIONS}
              value={groupBy}
              onChange={onGroupByChange}
              optionClassName={styles.groupByOption}
            />
          }
          grow="1"
          contentClassName={styles.cardContent}
        >
          <UsageChartView
            data={chartData}
            items={items}
            colorMapper={getColorMapper(groupBy)}
            formatXAxisLabel={formatXAxisLabel}
            tooltipHeader="Total minutes:"
            legendTitle={getLegendTitleByGroup(groupBy)}
            leftAxisLabel="Minutes"
          />
        </Card>
      </Box>
    </>
  );
};

export default UsageRunMinutesContent;
